import { ref } from 'vue'
import { defineStore } from 'pinia'
import { userApi } from '../api/user.api'
import { useQueryStore } from '@/shared/stores/query-param.store'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import { ActivationErrorCode, IdentityStatus, RegisterErrorCode } from '../types/user.enums'
import type { RouteLocationRaw } from 'vue-router'
import type { Client } from '@/modules/client/models/client.models'
import type { Identity, Profile, RegisterUser, UserSetting } from '../models/user.model'
import type { HttpResponse } from '@/services/http'
import type { ProfileRequest } from '../api/user.requests'

export const useUserStore = defineStore('user', () => {
  const queryStore = useQueryStore()
  const identity = ref<Identity>()
  const profile = ref<Profile>()
  const activeClient = ref<Client>()
  const clients = ref<Client[]>([])

  const clear = (): void => {
    identity.value = undefined
    profile.value = undefined
    activeClient.value = undefined
    clients.value = []
  }

  const loadIdentity = async (): Promise<boolean> => {
    const { data, error } = await userApi.getUserIdentity()
    if (!error && data) {
      identity.value = data
      if (data.status === IdentityStatus.Authenticated) {
        return true
      }
    }

    return false
  }

  const loadUser = async (): Promise<boolean> => {
    const { data, error } = await userApi.getUser()
    if (!error && data) {
      profile.value = data.profile
      clients.value = data.clients
      const client = queryStore.getClient(data.clients)
      activeClient.value = client ? client : data.activeClient

      return true
    }

    return false
  }

  const updateUserProfile = async (body: ProfileRequest): Promise<boolean> => {
    const { error } = await userApi.updateUserProfile(body)
    if (!error && profile.value) {
      profile.value.firstName = body.firstName
      profile.value.lastName = body.lastName
      profile.value.email = body.email
      profile.value.phone = body.phone

      return true
    }

    return false
  }

  const updateUserSetting = async (
    userSetting: UserSetting,
  ): Promise<HttpResponse<boolean, any>> => {
    const response = await userApi.updateUserSetting(userSetting)
    return {
      data: !response.error,
      error: response.error,
      status: response.status,
    }
  }

  const activateUser = async (code: string): Promise<HttpResponse<null, ActivationErrorCode>> => {
    return await userApi.activateUser(code.toLowerCase())
  }

  const registerUser = async (
    user: RegisterUser,
  ): Promise<HttpResponse<null, RegisterErrorCode>> => {
    return await userApi.registerUser(user)
  }

  return {
    identity,
    profile,
    clients,
    activeClient,
    clear,
    loadIdentity,
    loadUser,
    updateUserProfile,
    updateUserSetting,
    activateUser,
    registerUser,
  }
})
